import { CalendarDaysIcon, ChatBubbleLeftIcon, CheckCircleIcon, ClipboardDocumentCheckIcon, ClipboardDocumentListIcon, FlagIcon, HeartIcon, RectangleGroupIcon, SignalIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import { withFeature } from "@packages/core/access/featureAccess.ts";
import { hasFeature } from "@packages/core/session/features.ts";
import { hasProduct } from "@packages/core/session/products.ts";
import { hasAnyRole } from "@packages/core/session/roles.ts";
import { initActionItemsI18next } from "@domains/action-items/client/i18n/i18next.ts";
import { initEngagementI18next } from "@domains/engagement/client/i18n/i18next.ts";
import { initFeedbackI18next } from "@domains/feedback/client/i18n/i18next.ts";
import { RECOGNITION_LOCATIONS } from "@domains/good-vibes/client/components/goodVibesTracking.ts";
import { initMeetingsI18next } from "@domains/meetings/client/i18n/i18next.ts";
import { initPlansI18next } from "@domains/plans/client/i18n/i18next.ts";
import { isMeetingsInPerformanceEnabled } from "@domains/meetings/client/utils/meetingsInPerformanceAccess.ts";
import { REVIEW_GOOD_VIBE_SEGMENT } from "@domains/reviews/client/components/reviewRoutes.ts";
import { initReviewCyclesI18next } from "@domains/reviews/client/i18n/i18next.ts";
import { initSegmentsI18next } from "@domains/segments/client/i18n/i18next.ts";
import { initScopeI18next } from "@packages/core-client/scope/i18n/i18next.ts";
import type { AppDeferredRegistrationData, AppModuleRegisterFunction } from "@packages/core-client/registration/appModuleRegisterFunction.ts";
import { registerArea } from "@packages/core-client/registration/registerArea.tsx";
import type { FireflyRuntime } from "@squide/firefly";
import type { i18n } from "i18next";
import { TEAM_ACCESS } from "#shared/access.ts";
import { initI18next } from "./i18n/i18next.ts";

async function registerMsw(runtime: FireflyRuntime) {
    if (runtime.isMswEnabled) {
        const requestHandlers = (await import("./api/mocks/getRequestHandlers.ts")).getRequestHandlers();

        runtime.registerRequestHandlers(requestHandlers);
    }
}

function registerReviewDetailRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const reviewDetailArea = registerArea(runtime, "team-reviews-detail", "/team/:revieweeId/reviews/:reviewCycleId", ClipboardDocumentCheckIcon, i18nextInstance, { access: withFeature("PerformanceReviews", TEAM_ACCESS) });

    reviewDetailArea.registerPage({
        page: "TeamReviewDetail",
        lazy: async () => {
            const { ReviewDetailPage } = await import("./pages/reviews/detail/ReviewDetailPage.tsx");
            const { ReviewDetailErrorBoundary } = await import("./pages/reviews/detail/ReviewDetailErrorBoundary.tsx");

            return { element: <ReviewDetailPage />, errorElement: <ReviewDetailErrorBoundary /> };
        }
    });
}

function registerReviewShareRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const reviewShareArea = registerArea(runtime, "team-reviews-share", "/team/:revieweeId/reviews/:reviewCycleId/share", ClipboardDocumentCheckIcon, i18nextInstance, { access: withFeature("PerformanceReviews", TEAM_ACCESS) });

    reviewShareArea.registerPage({
        page: "TeamReviewShare",
        lazy: async () => {
            const { TeamReviewSharePage } = await import("./pages/reviews/share/TeamReviewSharePage.tsx");
            const { ReviewDetailErrorBoundary } = await import("./pages/reviews/detail/ReviewDetailErrorBoundary.tsx");

            return { element: <TeamReviewSharePage />, errorElement: <ReviewDetailErrorBoundary /> };
        }
    });
}

function registerReviewWriteRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const reviewWriteArea = registerArea(runtime, "team-reviews-write", "/team/:revieweeId/reviews/:reviewCycleId/:perspective", ClipboardDocumentCheckIcon, i18nextInstance, { access: withFeature("PerformanceReviews", TEAM_ACCESS) });

    reviewWriteArea.registerPage({
        page: "TeamReviewWrite",
        fullHeight: true,
        lazy: async () => {
            const { TeamReviewWritePage } = await import("./pages/reviews/write/TeamReviewWritePage.tsx");
            const { ReviewDetailErrorBoundary } = await import("./pages/reviews/detail/ReviewDetailErrorBoundary.tsx");

            return { element: <TeamReviewWritePage />, errorElement: <ReviewDetailErrorBoundary /> };
        }
    });

    reviewWriteArea.registerOverlay("good-vibe-detail", {
        segment: `${REVIEW_GOOD_VIBE_SEGMENT}/:recognitionId`,
        page: "TeamReviewWriteGoodVibeDetail",
        lazy: async () => {
            const { ReviewWriteGoodVibeRoute } = await import("./pages/reviews/write/ReviewWriteGoodVibeRoute.tsx");

            return { element: <ReviewWriteGoodVibeRoute /> };
        }
    });
}

async function loadTeamGoalProgressUpdateRoute() {
    const { TeamGoalProgressUpdateRoute } = await import("./pages/goals/detail/TeamGoalProgressUpdateRoute.tsx");

    return { element: <TeamGoalProgressUpdateRoute /> };
}

function registerGoalDetailRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const goalDetailArea = registerArea(runtime, "team-goals-detail", "/team/goals/:goalId", FlagIcon, i18nextInstance, { access: withFeature("Goals", TEAM_ACCESS) });

    goalDetailArea.registerPage({
        page: "TeamGoalDetail",
        lazy: async () => {
            const { TeamGoalDetailPage } = await import("./pages/goals/detail/TeamGoalDetailPage.tsx");
            const { TeamGoalDetailErrorBoundary } = await import("./pages/goals/detail/TeamGoalDetailErrorBoundary.tsx");

            return { element: <TeamGoalDetailPage />, errorElement: <TeamGoalDetailErrorBoundary /> };
        }
    });

    goalDetailArea.registerOverlay("progress-update", { segment: "update", page: "TeamGoalProgressUpdate", lazy: loadTeamGoalProgressUpdateRoute });
    goalDetailArea.registerOverlay("progress-update-edit", { segment: "update/:updateId", page: "TeamGoalProgressUpdate", lazy: loadTeamGoalProgressUpdateRoute });
}

function registerGoalEditRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const goalEditArea = registerArea(runtime, "team-goals-edit", "/team/goals/:goalId/edit", FlagIcon, i18nextInstance, { access: withFeature("Goals", TEAM_ACCESS) });

    goalEditArea.registerPage({
        page: "TeamGoalEdit",
        fullHeight: true,
        lazy: async () => {
            const { TeamGoalEditPage } = await import("./pages/goals/edit/TeamGoalEditPage.tsx");
            const { TeamGoalDetailErrorBoundary } = await import("./pages/goals/detail/TeamGoalDetailErrorBoundary.tsx");

            return { element: <TeamGoalEditPage />, errorElement: <TeamGoalDetailErrorBoundary /> };
        }
    });
}

async function loadPlanFormPage() {
    const { PlansFlagGate } = await import("@domains/plans/client/components/PlansFlagGate.tsx");
    const { PlanFormPage } = await import("@domains/plans/client/components/PlanFormPage.tsx");
    const { PlanDetailErrorBoundary } = await import("@domains/plans/client/components/PlanDetailErrorBoundary.tsx");

    return {
        element: (
            <PlansFlagGate>
                <PlanFormPage />
            </PlansFlagGate>
        ),
        errorElement: <PlanDetailErrorBoundary />
    };
}

function registerPlanRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const newPlanArea = registerArea(runtime, "plans-new", "/plans/new", ClipboardDocumentListIcon, i18nextInstance, { access: withFeature("Plans") });

    newPlanArea.registerPage({ page: "NewPlan", fullHeight: true, lazy: loadPlanFormPage });

    const planArea = registerArea(runtime, "plans-detail", "/plans/:planId", ClipboardDocumentListIcon, i18nextInstance, { access: withFeature("Plans") });

    planArea.registerPage({
        page: "PlanDetail",
        lazy: async () => {
            const { PlansFlagGate } = await import("@domains/plans/client/components/PlansFlagGate.tsx");
            const { PlanPage } = await import("@domains/plans/client/components/PlanPage.tsx");
            const { PlanDetailErrorBoundary } = await import("@domains/plans/client/components/PlanDetailErrorBoundary.tsx");

            return {
                element: (
                    <PlansFlagGate>
                        <PlanPage />
                    </PlansFlagGate>
                ),
                errorElement: <PlanDetailErrorBoundary />
            };
        }
    });

    const editPlanArea = registerArea(runtime, "plans-edit", "/plans/:planId/edit", ClipboardDocumentListIcon, i18nextInstance, { access: withFeature("Plans") });

    editPlanArea.registerPage({ page: "EditPlan", fullHeight: true, lazy: loadPlanFormPage });
}

function registerEngagementDetailRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const engagementDetailArea = registerArea(runtime, "team-engagement-detail", "/team/engagement/:metricId", SignalIcon, i18nextInstance, { access: withFeature("Engagement", TEAM_ACCESS) });

    engagementDetailArea.registerPage({
        page: "TeamEngagementMetricDetail",
        lazy: async () => {
            const { EngagementMetricDetailPage } = await import("./pages/engagement/detail/EngagementMetricDetailPage.tsx");
            const { EngagementMetricDetailErrorBoundary } = await import("./pages/engagement/detail/EngagementMetricDetailErrorBoundary.tsx");

            return { element: <EngagementMetricDetailPage />, errorElement: <EngagementMetricDetailErrorBoundary /> };
        }
    });
}

export const registerTeam: AppModuleRegisterFunction = async runtime => {
    const i18nextInstance = initI18next(runtime);
    initSegmentsI18next(runtime);
    initScopeI18next(runtime);
    initEngagementI18next(runtime);
    initMeetingsI18next(runtime);
    initFeedbackI18next(runtime);
    initReviewCyclesI18next(runtime);
    initPlansI18next(runtime);
    initActionItemsI18next(runtime);

    await registerMsw(runtime);

    const area = registerArea(runtime, "team", "/team", UserGroupIcon, i18nextInstance, { access: TEAM_ACCESS });

    runtime.registerRoute({
        path: "/teams/*",
        lazy: async () => {
            const { TeamsLegacyRedirect } = await import("./TeamsLegacyRedirect.tsx");

            return { element: <TeamsLegacyRedirect /> };
        }
    });

    const page = area.registerTabsArea("main", { preserveSearchParams: ["team-scope", "team-segment"], chrome: async () => (await import("./components/teamTabsChrome.ts")).TEAM_TABS_CHROME });

    const overview = page.registerRoute("overview", {
        index: true,
        page: "TeamOverview",
        lazy: async () => {
            const { TeamOverviewPage } = await import("./pages/overview/TeamOverviewPage.tsx");

            return { element: <TeamOverviewPage /> };
        }
    });

    const engagement = page.registerRoute("engagement", {
        path: "/engagement",
        page: "TeamEngagement",
        access: withFeature("Engagement", TEAM_ACCESS),
        lazy: async () => {
            const { TeamEngagementPage } = await import("./pages/engagement/TeamEngagementPage.tsx");

            return { element: <TeamEngagementPage /> };
        }
    });

    const meetings = page.registerRoute("meetings", {
        path: "/meetings",
        page: "TeamMeetings",
        access: withFeature("Meetings", TEAM_ACCESS),
        lazy: async () => {
            const { TeamMeetingsPage } = await import("./pages/meetings/TeamMeetingsPage.tsx");

            return { element: <TeamMeetingsPage /> };
        }
    });

    const goodVibes = page.registerRoute("good-vibes", {
        path: "/good-vibes",
        page: "TeamGoodVibes",
        access: withFeature("Recognition", TEAM_ACCESS),
        lazy: async () => {
            const { TeamGoodVibesPage } = await import("./pages/good-vibes/TeamGoodVibesPage.tsx");

            return { element: <TeamGoodVibesPage /> };
        }
    });

    goodVibes.registerOverlay("detail", {
        segment: ":recognitionId",
        page: "TeamGoodVibeDetail",
        lazy: async () => {
            const { GoodVibeDetailRoute } = await import("./pages/good-vibes/GoodVibeDetailRoute.tsx");

            return { element: <GoodVibeDetailRoute trackingLocation={RECOGNITION_LOCATIONS.Team} /> };
        }
    });

    const feedback = page.registerRoute("feedback", {
        path: "/feedback",
        page: "TeamFeedback",
        access: withFeature("Engagement", TEAM_ACCESS),
        lazy: async () => {
            const { TeamFeedbackPage } = await import("./pages/feedback/TeamFeedbackPage.tsx");

            return { element: <TeamFeedbackPage /> };
        }
    });

    runtime.registerRoute({ path: "/team/feedback/:feedbackId", element: null }, { parentPath: "/team/feedback" });

    const reviews = page.registerRoute("reviews", {
        path: "/reviews",
        page: "TeamReviews",
        access: withFeature("PerformanceReviews", TEAM_ACCESS),
        lazy: async () => {
            const { TeamReviewsPage } = await import("./pages/reviews/list/TeamReviewsPage.tsx");

            return { element: <TeamReviewsPage /> };
        }
    });

    const goals = page.registerRoute("goals", {
        path: "/goals",
        page: "TeamGoals",
        access: withFeature("Goals", TEAM_ACCESS),
        lazy: async () => {
            const { TeamGoalsPage } = await import("./pages/goals/list/TeamGoalsPage.tsx");

            return { element: <TeamGoalsPage /> };
        }
    });

    goals.registerOverlay("progress-update", { segment: "update/:goalId", page: "TeamGoalsProgressUpdate", lazy: loadTeamGoalProgressUpdateRoute });

    const plans = page.registerRoute("plans", {
        path: "/plans",
        page: "TeamPlans",
        access: withFeature("Plans", TEAM_ACCESS),
        lazy: async () => {
            const { TeamPlansPage } = await import("./pages/plans/TeamPlansPage.tsx");

            return { element: <TeamPlansPage /> };
        }
    });

    const actionItems = page.registerRoute("action-items", {
        path: "/action-items",
        page: "TeamActionItems",
        access: TEAM_ACCESS,
        lazy: async () => {
            const { TeamActionItemsPage } = await import("./pages/action-items/TeamActionItemsPage.tsx");

            return { element: <TeamActionItemsPage /> };
        }
    });

    actionItems.registerOverlay("detail", {
        segment: ":actionItemId",
        page: "TeamActionItemDetail",
        lazy: async () => {
            const { TeamActionItemRoute } = await import("./pages/action-items/TeamActionItemRoute.tsx");

            return { element: <TeamActionItemRoute /> };
        }
    });

    registerReviewDetailRoutes(runtime, i18nextInstance);
    registerReviewShareRoutes(runtime, i18nextInstance);
    registerReviewWriteRoutes(runtime, i18nextInstance);
    registerGoalDetailRoutes(runtime, i18nextInstance);
    registerGoalEditRoutes(runtime, i18nextInstance);
    registerEngagementDetailRoutes(runtime, i18nextInstance);
    registerPlanRoutes(runtime, i18nextInstance);

    return (deferredRuntime: FireflyRuntime, { session }: AppDeferredRegistrationData) => {
        if (!hasAnyRole(session, TEAM_ACCESS)) {
            return;
        }

        overview.registerLink({ priority: 1000, icon: RectangleGroupIcon });

        if (hasFeature("Engagement")(session)) {
            engagement.registerLink({ priority: 999, icon: SignalIcon });
            feedback.registerLink({ priority: 996, icon: ChatBubbleLeftIcon });
        }

        if (hasFeature("Recognition")(session)) {
            goodVibes.registerLink({ priority: 997, icon: HeartIcon });
        }

        if (hasFeature("PerformanceReviews")(session)) {
            reviews.registerLink({ priority: 995, icon: ClipboardDocumentCheckIcon });
        }

        if (hasFeature("Goals")(session)) {
            goals.registerLink({ priority: 994, icon: FlagIcon });
        }

        if (isMeetingsInPerformanceEnabled(deferredRuntime.getFeatureFlag("meetings-in-performance", false), session) && hasFeature("Meetings")(session)) {
            meetings.registerLink({ priority: 993, icon: CalendarDaysIcon });
        }

        if (deferredRuntime.getFeatureFlag("plans", false) && hasFeature("Plans")(session)) {
            plans.registerLink({ priority: 992, icon: ClipboardDocumentListIcon });
        }

        if (deferredRuntime.getFeatureFlag("action-items", false) && hasProduct(session, "wpm")) {
            actionItems.registerLink({ priority: 991, icon: CheckCircleIcon });
        }

        area.registerToTopNav({ priority: 200 });
    };
};
